import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Gift, CreditCard, Landmark, Tag, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { api, img, money, errMsg } from "../lib/api";
import { useCart, useAuth, useSite } from "../lib/store";
import { useSeo } from "../components/Seo";
import { ShippingForm, shipPrice } from "../components/ShippingForm";

const BILL = [["name", "Jméno a příjmení"], ["company", "Firma / školka (nepovinné)"], ["street", "Ulice a č.p."], ["city", "Město"], ["zip", "PSČ"], ["ico", "IČO (nepovinné)"], ["dic", "DIČ (nepovinné)"], ["phone", "Telefon (nepovinné)"]];

export default function Cart() {
  useSeo({ title: "Košík", noindex: true });
  const { items, remove, setQty, total, clear, syncPhysical } = useCart();
  const { user } = useAuth();
  const { site } = useSite();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [method, setMethod] = useState("bank");
  const [billing, setBilling] = useState({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState(() => JSON.parse(sessionStorage.getItem("mps_ship") || '{"carrier":"zasilkovna","mode":"zbox"}'));
  useEffect(() => {
    sessionStorage.setItem("mps_ship", JSON.stringify(ship));
  }, [ship]);
  const slugs = items.filter((i) => i.type === "product").map((i) => i.slug).join(",");
  useEffect(() => {
    if (!slugs) return;
    Promise.all(slugs.split(",").map((s) => api.get(`/products/${s}`).then((r) => [s, !!r.data.physical]).catch(() => null))).then((r) => syncPhysical(Object.fromEntries(r.filter(Boolean))));
  }, [slugs]); // eslint-disable-line react-hooks/exhaustive-deps
  const physical = items.some((i) => i.type === "product" && i.physical);
  const shipping = physical ? shipPrice(ship) : 0;
  useEffect(() => {
    if (user) setBilling({ name: user.name, ...(user.billing || {}) });
  }, [user]);
  const prodTotal = items.filter((i) => i.type === "product").reduce((s, i) => s + i.price, 0);
  const applyCode = async () => {
    try {
      const { data } = await api.post("/checkout/validate-code", { code, subtotal_products: prodTotal, subtotal: total });
      setApplied(data);
      toast.success(`Kód uplatněn: ${data.label}`);
    } catch (e) {
      setApplied(null);
      toast.error(errMsg(e));
    }
  };
  const final = Math.max(0, total + shipping - (applied?.amount || 0));
  const shipData = () => ({ ...Object.fromEntries(["name", "street", "city", "zip", "phone"].map((k) => [k, billing[k] || ""])), ...ship });
  const submit = async () => {
    if (!user) return nav("/prihlaseni?next=/kosik");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", { items: items.map((i) => ({ type: i.type, product_id: i.product_id, value: i.value, qty: i.qty || 1 })), code: applied?.code, payment_method: method, billing, shipping: physical ? shipData() : {}, consent });
      clear();
      if (data.redirect) window.location.href = data.redirect;
      else nav(`/objednavka/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!items.length) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="cart-empty">
      <span className="mx-auto w-28 h-28 rounded-full bg-[var(--pink-soft)] grid place-items-center floaty"><ShoppingBag className="w-12 h-12 text-[var(--pink)]" /></span>
      <h1 className="text-4xl font-semibold mt-6">Košík je zatím prázdný</h1>
      <p className="text-[var(--muted)] mt-2 mb-8">Vyberte si z materiálů nebo darujte dárkový poukaz.</p>
      <div className="flex flex-wrap gap-3 justify-center"><Link to="/eshop" className="btn-pink">Do e-shopu</Link><Link to="/darkove-poukazy" className="btn-ghost"><Gift className="w-4 h-4" /> Dárkové poukazy</Link></div>
    </div>
  );
  const step = !user ? 1 : 2;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <h1 className="text-4xl sm:text-5xl font-semibold"><span className="squiggle">Váš košík</span></h1>
        <ol className="flex items-center gap-2 text-sm font-bold" data-testid="cart-steps">
          {["Košík", "Přihlášení", "Údaje & platba"].map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full grid place-items-center ${i <= step ? "bg-[var(--pink)] text-white" : "bg-[var(--blush)] text-[var(--muted)]"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>{i < 2 && <span className="w-6 h-px bg-[#f0cfe0]" />}
            </li>
          ))}
        </ol>
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
      <div>
        <div className="space-y-3">
          {items.map((i, k) => (
            <div key={k} className="flex items-center gap-4 rounded-[28px] bg-white border border-[#f6e3ec] p-3 sm:p-4 hover:shadow-[0_16px_30px_-20px_rgba(212,86,140,.4)] transition-shadow" data-testid={`cart-item-${k}`}>
              {i.type === "product" ? <img src={img(i.image)} alt="" className="w-20 h-20 rounded-2xl object-cover" /> : <span className="w-20 h-20 rounded-2xl bg-[var(--sun-soft)] grid place-items-center"><Gift className="text-[var(--pink)]" /></span>}
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg truncate">{i.name}</p>
                {i.type === "voucher" && (
                  <div className="flex items-center gap-2 mt-1 text-sm">Počet: <input type="number" min={1} value={i.qty} onChange={(e) => setQty(k, +e.target.value)} className="field !w-20 !py-1" data-testid={`cart-qty-${k}`} /></div>
                )}
              </div>
              <span className="font-extrabold">{money(i.price * (i.qty || 1))}</span>
              <button onClick={() => remove(k)} className="w-10 h-10 rounded-full hover:bg-[var(--pink-soft)] grid place-items-center" data-testid={`cart-remove-${k}`} aria-label="Odebrat"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-[32px] border border-[#f6e3ec] p-6 sm:p-8">
          <h2 className="text-2xl font-semibold mb-1">Fakturační údaje</h2>
          <p className="text-sm text-[var(--muted)] mb-5">Použijeme je na faktuře a uložíme do vašeho účtu.</p>
          {!user && <p className="text-sm mb-4 bg-[var(--sun-soft)] rounded-2xl p-4">Pro dokončení nákupu se prosím <Link to="/prihlaseni?next=/kosik" className="underline font-bold">přihlaste</Link> nebo <Link to="/registrace?next=/kosik" className="underline font-bold">zaregistrujte</Link> – soubory pak najdete ve svém účtu.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {BILL.map(([k, l]) => <input key={k} placeholder={l} value={billing[k] || ""} onChange={(e) => setBilling({ ...billing, [k]: e.target.value })} className="field" data-testid={`billing-${k}-input`} />)}
          </div>
        </div>
        {physical && <ShippingForm ship={ship} setShip={setShip} billing={billing} />}
      </div>
      <aside className="lg:sticky lg:top-28 self-start rounded-[32px] bg-[var(--blush)] p-6 sm:p-8 space-y-6 relative overflow-hidden">
        <div>
          <p className="font-display text-lg mb-2 flex items-center gap-2"><Tag className="w-4 h-4" /> Slevový kód nebo poukaz</p>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="KÓD" className="field !rounded-full" data-testid="cart-code-input" />
            <button onClick={applyCode} className="btn-ghost !px-4" data-testid="cart-code-apply-btn">Použít</button>
          </div>
          {applied && <p className="text-sm text-[var(--teal)] font-bold mt-2" data-testid="cart-code-applied">{applied.label}: −{money(applied.amount)}</p>}
        </div>
        <div>
          <p className="font-display text-lg mb-2">Způsob platby</p>
          {[["bank", Landmark, "Bankovní převod / QR platba", true], ["comgate", CreditCard, "Platba kartou (Comgate)", site?.payments?.comgate]].map(([v, I, l, ok]) => (
            <label key={v} className={`flex items-center gap-3 rounded-2xl bg-white p-4 mb-2 border-2 cursor-pointer ${method === v ? "border-[var(--pink)]" : "border-transparent"} ${!ok ? "opacity-50 cursor-not-allowed" : ""}`} data-testid={`payment-${v}`}>
              <input type="radio" disabled={!ok} checked={method === v} onChange={() => setMethod(v)} className="accent-[var(--pink)]" />
              <I className="w-4 h-4" /> <span className="font-semibold text-sm">{l}</span>{!ok && <span className="text-xs ml-auto">brzy</span>}
            </label>
          ))}
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span>Mezisoučet</span><span>{money(total)}</span></div>
          {physical && <div className="flex justify-between" data-testid="cart-shipping-price"><span>Doprava</span><span>{money(shipping)}</span></div>}
          {applied && <div className="flex justify-between text-[var(--teal)]"><span>Sleva</span><span>−{money(applied.amount)}</span></div>}
          <div className="flex justify-between text-2xl font-extrabold pt-3"><span>Celkem</span><span data-testid="cart-total">{money(final)}</span></div>
        </div>
        <label className="flex gap-2 text-xs text-[var(--muted)]">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[var(--pink)]" data-testid="cart-consent-checkbox" />
          <span>Souhlasím s <Link to="/obchodni-podminky" className="underline">obchodními podmínkami</Link>{items.some((i) => !i.physical) ? " a výslovně žádám o dodání digitálního obsahu před uplynutím lhůty pro odstoupení" : ""}.</span>
        </label>
        <button onClick={submit} disabled={busy || (user && !consent)} className="btn-pink w-full !py-4 disabled:opacity-50" data-testid="cart-submit-btn">{busy ? "Odesílám…" : user ? "Objednat s povinností platby" : "Přihlásit a objednat"}</button>
      </aside>
      </div>
    </div>
  );
}
